import {
  LegacyCard,
  IndexTable,
  Text,
  Link,
  LegacyStack,
  Pagination,
  Divider,
  ExceptionList,
  Card,
  HorizontalStack,
  Spinner,
  Icon,
} from '@shopify/polaris';
import { StatusBadge } from '../Common/StatusBadge';
import fillZeros from '../../../helpers/fillZeros';
import { CancelMajor, HorizontalDotsMinor } from '@shopify/polaris-icons';
import moment from 'moment';

// Nombre completo del depósito (con su "EPH 1014 |", que Aitor quiere conservar) en una
// celda estrecha: hasta dos líneas y ellipsis si aún no cabe. line-clamp va inline porque
// el Tailwind del repo (3.2) todavía no lo trae de serie.
const depositNameStyle = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  maxWidth: '9rem',
  whiteSpace: 'normal',
};

const ListTransfers = ({
  data,
  isLoading,
  error,
  fetchNextPage,
  hasNextPage,
  fetchPreviousPage,
  hasPreviousPage,
}) => {
    const showDepositEndpoint = (id) => `/transfers/${id}/`;

    const headingMarkup = [
      {},
      { title: 'Nombre' },
      { title: 'Estado' },
      { title: 'Fecha' },
      { title: 'Origen' },
      { title: 'Destino' },
      { title: 'Productos' },
      { title: 'Uds enviadas' },
      { title: 'Uds recibidas' },
    ];

    const rowMarkup = (
      {
        id,
        status,
        name,
        createdAt,
        stockQty,
        stockReceivedQty,
        stockLostQty,
        productsQty,
        originDeposit,
        destinationDeposit,
      },
      index,
    ) => {
      // Mercancía que salió y no está ni recibida ni dada por extraviada: sigue en el limbo
      // de la cuenta 210. En tránsito es normal (todavía no ha llegado); en un traspaso
      // cerrado no debería pasar nunca, y es justo lo que interesa detectar de un vistazo.
      const sinRepartir = (stockQty ?? 0) - (stockReceivedQty ?? 0) - (stockLostQty ?? 0);
      const descuadrado = status === 'CLOSED' && sinRepartir !== 0;

      return (
      <IndexTable.Row id={id} key={id} position={index}>
        <IndexTable.Cell>
          <Link dataPrimaryLink url={showDepositEndpoint(id)}></Link>#{fillZeros(id, 4)}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="bold" as="span">
            {name}
          </Text>
          {status === 'PENDING' && (
            <ExceptionList
              items={[
                {
                  icon: HorizontalDotsMinor,
                  status: 'warning',
                  title: 'Pendiente Confirmación',
                },
              ]}
            />
          )}
        </IndexTable.Cell>
        <IndexTable.Cell>
          <StatusBadge type="transfer" status={status} />
        </IndexTable.Cell>
        <IndexTable.Cell>{moment(createdAt).format('DD/MM/YYYY')}</IndexTable.Cell>
        <IndexTable.Cell>
          <span title={originDeposit.name} style={depositNameStyle}>
            <Text variant="bodySm">{originDeposit.name}</Text>
          </span>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <span title={destinationDeposit.name} style={depositNameStyle}>
            <Text variant="bodySm">{destinationDeposit.name}</Text>
          </span>
        </IndexTable.Cell>
        <IndexTable.Cell>{productsQty ? productsQty : 0}</IndexTable.Cell>
        <IndexTable.Cell>{stockQty ? stockQty : 0}</IndexTable.Cell>
        <IndexTable.Cell>
          <Text variant="bodySm" color={descuadrado ? 'critical' : undefined} fontWeight={descuadrado ? 'bold' : undefined}>
            {stockReceivedQty ? stockReceivedQty : 0}
            {descuadrado && ` (${sinRepartir > 0 ? '−' : '+'}${Math.abs(sinRepartir)})`}
          </Text>
        </IndexTable.Cell>
      </IndexTable.Row>
      );
    };

    return (
      <>
        {/* Las dos últimas cabeceras ("Uds enviadas" / "Uds recibidas") parten en dos líneas:
            son columnas de un número de dos cifras y el título a una línea les regalaba el
            triple de ancho del que necesitan. Polaris no expone esto por props, de ahí el CSS. */}
        <style>{`
          .transfers-list .Polaris-IndexTable__TableHeading:nth-last-child(-n+2) {
            white-space: normal;
            max-width: 5.5rem;
            line-height: 1.15;
          }
        `}</style>
        {isLoading ? (
          <Card>
            <HorizontalStack align="center" blockAlign="center">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '.5rem',
                  padding: '1.5rem',
                }}
              >
                <Spinner accessibilityLabel="Cargando inventarios" size="large" />
                <Text variant="bodySm">Cargando inventarios...</Text>
              </div>
            </HorizontalStack>
          </Card>
        ) : error ? (
          <Card>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '.5rem',
                padding: '1.5rem',
              }}
            >
              <Icon source={CancelMajor} color="critical" size="large" />
              <Text variant="bodyMd">Error al cargar los inventarios</Text>
            </div>
          </Card>
        ) : (
          <>
            <div className="transfers-list">
            <IndexTable
              resourceName={{
                singular: 'Traspaso',
                plural: 'Traspasos',
              }}
              itemCount={data?.length || 0}
              headings={headingMarkup}
              selectable={false}
            >
              {data?.map((r, k) => rowMarkup(r, k))}
            </IndexTable>
            </div>
            <Divider borderStyle="divider" />
            <LegacyCard.Section>
              <LegacyStack distribution="center">
                <Pagination
                  hasPrevious={!!hasPreviousPage}
                  onPrevious={hasPreviousPage ? fetchPreviousPage : undefined}
                  hasNext={!!hasNextPage}
                  onNext={hasNextPage ? fetchNextPage : undefined}
                />
              </LegacyStack>
            </LegacyCard.Section>
          </>
        )}
      </>
    );
};

export default ListTransfers;
